import React, { createContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid'; // For unique IDs for each toast
import Toast from '../components/ui/Toast'; // Import the Toast component

// Create the Toast Context
export const ToastContext = createContext();

/**
 * Provides a global toast notification system.
 * Manages an array of toasts and their display/removal.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider.
 */
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    /**
     * Displays a new toast notification.
     * @param {string} message - The message to display in the toast.
     * @param {'success' | 'error' | 'info'} [type='info'] - The type of toast (for styling).
     * @param {number} [duration=5000] - How long the toast should be visible in milliseconds.
     */
    const showToast = useCallback((message, type = 'info', duration = 5000) => {
        const id = uuidv4(); // Generate a unique ID for the toast
        setToasts((prevToasts) => [...prevToasts, { id, message, type }]);

        // Auto-hide the toast after a duration
        if (duration > 0) {
            setTimeout(() => {
                setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
            }, duration);
        }
    }, []);

    const handleCloseToast = useCallback((id) => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {/* Toast Container - fixed position on the screen */}
            <div className="fixed top-4 right-4 z-50 w-full max-w-xs pointer-events-none">
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        id={toast.id}
                        message={toast.message}
                        type={toast.type}
                        onClose={handleCloseToast}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};
